import { PensionType } from '../enums';

interface PensionThresholds {
  tooLow?: number;
  averageMin?: number;
  averageMax?: number;
  tooHigh?: number;
}

interface PensionValidationEntry {
  growthRate: number;
  thresholds: PensionThresholds;
}

const PENSION_VALIDATION: Record<PensionType, PensionValidationEntry> = {
  [PensionType.standard]: {
    growthRate: 0.02,
    thresholds: {
      tooLow: 10000,
      averageMin: 15000,
      averageMax: 100000,
      tooHigh: 200000,
    },
  },
  [PensionType.early]: {
    growthRate: 0.02,
    thresholds: {
      tooLow: 8000,
      averageMin: 12000,
      averageMax: 80000,
      tooHigh: 160000,
    },
  },
  [PensionType.late]: {
    growthRate: 0.02,
    thresholds: {
      tooLow: 15000,
      averageMin: 20000,
      averageMax: 120000,
      tooHigh: 250000,
    },
  },
  [PensionType.bridge]: {
    growthRate: 0,
    thresholds: {
      tooLow: 5000,
    },
  },
  [PensionType.none]: {
    growthRate: 0,
    thresholds: {},
  },
};

// Freeze the records so they can't be mutated at runtime
Object.freeze(PENSION_VALIDATION);
Object.values(PENSION_VALIDATION).forEach((entry) => {
  Object.freeze(entry);
  Object.freeze(entry.thresholds);
});

export { PENSION_VALIDATION };

/**
 * Return the default { growthRate, thresholds } for a PensionType.
 */
export function getPensionDefaults(type: PensionType): PensionValidationEntry {
  return PENSION_VALIDATION[type];
}

/**
 * Classify a monthly pension amount against the thresholds for `type`.
 * Returns 'tooLow' | 'average' | 'tooHigh' | undefined
 */
export function getPensionThresholdLabel(
  type: PensionType,
  monthlyAmount: number,
): 'tooLow' | 'average' | 'tooHigh' | undefined {
  const thresholds = PENSION_VALIDATION[type].thresholds;
  const tooLow = thresholds.tooLow;
  if (tooLow == null) {
    // "none" type has no thresholds
    return undefined;
  }
  if (monthlyAmount < tooLow) {
    return 'tooLow';
  }
  const tooHigh = thresholds.tooHigh;
  if (tooHigh != null && monthlyAmount >= tooHigh) {
    return 'tooHigh';
  }
  return 'average';
}
